import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import { categoryForSlug, fetchBrandsInCategoryAndCountry, lookupCountry } from '@/api';
import BrandGrid from '@/components/brand-grid';
import BrowseMore from '@/components/browse-more';
import { heroPhotoPath } from '@/components/categories-grid';
import HowItWorks from '@/components/how-it-works';
import MaintenanceNotice, { maintenancePageProps } from '@/components/maintenance-notice';
import SEO from '@/components/seo';
import {
  Button,
  Eyebrow,
  HeroBackground,
  HeroTitle,
  Lead,
  Section,
  SectionContainer,
  sectionTitleStyle,
} from '@/components/ui';
import config from '@/config';
import i18n from '@/i18n.json';
import { loadMessages } from '@/lib/messages';
import { up } from '@/themes';
import { flagForCountryCode, parseIPAddress } from '@/utils';
import { giftCardPhrase } from '@/utils/category';
import { countryNameInSentence } from '@/utils/country-name';
import { shareImage } from '@/utils/share-image';

const Select = dynamic(() => import('@/components/select'), { ssr: false });

const Hero = styled.section`
  isolation: isolate;
  overflow: hidden;
  position: relative;
`;

/* The marketing split hero: copy on the left, the photo on the right from 1024px, below it before. */
const HeroGrid = styled(SectionContainer)`
  align-items: center;
  display: grid;
  gap: 40px;
  padding-bottom: 48px;
  padding-top: 56px;

  ${up.sm} {
    gap: 48px;
    padding-bottom: 72px;
    padding-top: 88px;
  }

  ${up.lg} {
    gap: 64px;
    grid-template-columns: ${({ $split }) => ($split ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'none')};
  }
`;

const HeroText = styled.div`
  max-width: 672px;
`;

const Title = styled(HeroTitle)`
  margin-top: 16px;
`;

const Intro = styled(Lead)`
  margin-top: 24px;
`;

const HeroActions = styled.div`
  margin-top: 32px;
`;

/*
 * The category's photo (docs/image-credits.md), the same one as on its card.
 * 16:10 while it sits under the copy, so the brands stay close; 4:3 beside it.
 */
const HeroPhoto = styled.div`
  aspect-ratio: 16 / 10;
  background-color: var(--muted);
  border-radius: var(--radius-3xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;

  ${up.lg} {
    aspect-ratio: 4 / 3;
  }

  picture {
    display: block;
    height: 100%;
  }

  img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const HERO_WIDTH = 1200;
const HERO_HEIGHT = 900;

/* Full width under the copy; half of the 1280px container, less the 64px gap, beside it. */
const HERO_SIZES = [
  '(min-width: 1280px) 576px',
  '(min-width: 1024px) calc((100vw - 128px) / 2)',
  '(min-width: 640px) calc(100vw - 48px)',
  'calc(100vw - 32px)',
].join(', ');

const GridHeader = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;

  ${up.sm} {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const GridTitle = styled.h2`
  ${sectionTitleStyle};
`;

const CountryPicker = styled(Select)`
  margin-bottom: 0;
  min-width: 260px;
  width: 100%;

  ${up.sm} {
    width: auto;
  }
`;

/* Keeps the row height while the select (client-only) loads. */
const PickerSlot = styled.div`
  min-height: 44px;
  width: 100%;

  ${up.sm} {
    width: auto;
  }
`;

const Index = ({ category, brands, canonicalUrl, countries, countryId }) => {
  const router = useRouter();
  const t = useTranslations('shop');
  const cT = useTranslations('categories');
  const heading = giftCardPhrase(cT, category.slug, router.locale);

  const optionFor = (c) => ({
    value: c.id,
    label: `${c.id === 'XG' ? '🌎' : flagForCountryCode(c.id)} ${c.name}`,
    ...c,
  });

  let country = brands.length ? brands[0].country : countries.find((c) => c.id === countryId);
  if (!country) {
    country = {
      id: 'US',
      name: 'United States',
      slug: 'united-states',
    };
  }

  const inCountry = countryNameInSentence(country, router.locale);
  const browseUrl = `/buy/${country.slug}/${category.slug}`;
  const photo = heroPhotoPath(category.slug);

  const onChangeCountry = (country) => {
    router.push(`/buy/${country.slug}/${category.slug}`);
  };

  return (
    <>
      <SEO
        title={t('seoTitle', { category: heading })}
        description={t('seoDescription', { category: heading })}
        url={canonicalUrl}
        hreflangPath={`/shop/${category.slug}`}
        imageUrl={shareImage.category(category.slug, router.locale)}
        imageAlt={heading}
      />
      <Hero>
        <HeroBackground />
        <HeroGrid $split={!!photo}>
          <HeroText>
            <Eyebrow>{t('eyebrow')}</Eyebrow>
            <Title>{heading}</Title>
            <Intro $large>{t('intro')}</Intro>
            <HeroActions>
              <Button $size="pill" href={browseUrl}>
                {t('browse', { country: inCountry })}
              </Button>
            </HeroActions>
          </HeroText>
          {photo && (
            <HeroPhoto>
              {/* Decorative, and the largest element above the fold: loaded first, never lazily. */}
              <picture>
                <source
                  sizes={HERO_SIZES}
                  srcSet={`${photo}-720.avif 720w, ${photo}.avif ${HERO_WIDTH}w`}
                  type="image/avif"
                />
                <Image
                  alt=""
                  fetchPriority="high"
                  height={HERO_HEIGHT}
                  loading="eager"
                  src={`${photo}.webp`}
                  unoptimized
                  width={HERO_WIDTH}
                />
              </picture>
            </HeroPhoto>
          )}
        </HeroGrid>
      </Hero>
      <Section $spacing="compact">
        <GridHeader>
          <GridTitle>{t('availableIn', { country: inCountry })}</GridTitle>
          <PickerSlot>
            <CountryPicker
              aria-label={t('selectCountry')}
              instanceId="shop-country-select"
              isSearchable
              options={countries.map(optionFor)}
              value={optionFor(country)}
              onChange={onChangeCountry}
              placeholder={t('selectCountry')}
            />
          </PickerSlot>
        </GridHeader>
        <BrandGrid
          brands={brands}
          category={category}
          country={country}
          onReset={() => router.push(`/buy/${country.slug}`)}
          showEmptyState={!brands.length}
        />
      </Section>
      <BrowseMore exclude={[category.slug]} />
      <HowItWorks />
    </>
  );
};

export async function getServerSideProps({ params, query, req, locale }) {
  const found = await categoryForSlug(params.slug);

  if (!found) {
    return { notFound: true };
  }

  if (config.inMaintenanceMode) {
    return maintenancePageProps(locale);
  }

  //TODO: Error handling and log to Bugsnag
  const ipAddress = query.ip || parseIPAddress(req);
  const countryId = await lookupCountry(ipAddress);
  const brands = await fetchBrandsInCategoryAndCountry({
    countryId,
    categoryId: found.id,
    $limit: 12,
  });

  const canonicalUrl = `${config.rootUrl}${locale === i18n.defaultLocale ? '' : `/${locale}`}/shop/${found.slug}`;

  return {
    props: {
      countryId,
      // Only what the page renders: the API's own titles and descriptions are not used.
      category: { id: found.id, name: found.name, slug: found.slug },
      brands: brands.map(({ id, slug, name, imageUrl, backgroundColor, type, country }) => ({
        id,
        slug,
        name,
        imageUrl,
        backgroundColor: backgroundColor ?? null,
        type: type ?? null,
        country: { id: country.id, name: country.name, slug: country.slug },
      })),
      canonicalUrl,
      messages: loadMessages(locale),
    },
  };
}

const ShopPage = (props) => (props.maintenance ? <MaintenanceNotice /> : <Index {...props} />);

export default ShopPage;
